import { CandidateRole } from "src/app/models/enum";
import {
	AssessmentConfigRes,
	ManeuverOutcomeDraft,
	ManeuverOutcomeValue,
} from "src/app/models/models";
import { OutcomeSlot } from "./ts1-rating-outcome-interaction";
import { step3PfColumnOutcomeSlot, step3PmColumnOutcomeSlot } from "./ts1-rating-review.utils";

export type Ts1V3PreviewEvaluationRow = {
	assessmentId: string;
	rate: number;
	candidateId?: string;
	candidateRole?: string | null;
	maneuverCode: string;
};

/**
 * N/A/disabled OBs are always excluded from TS1-v3 How Many / How Often.
 */
export function shouldExcludeDisabledObForTs1V3(d: {
	isDisabled?: boolean | null;
	activeAttempt?: number | null;
}): boolean {
	return !!d.isDisabled;
}

function hasRole(role?: string | null): boolean {
	return typeof role === "string" && role.trim().length > 0;
}

function rolePreferenceScore(role: string | null | undefined, preferredRole?: string): number {
	if (!hasRole(role)) return -1;
	if (preferredRole && role === preferredRole) return 2;
	if (role === CandidateRole.PF || role === CandidateRole.PM) return 1;
	return 0;
}

function shouldReplaceExisting(
	existing: Ts1V3PreviewEvaluationRow,
	next: Ts1V3PreviewEvaluationRow,
	preferredRole?: string,
): boolean {
	const existingScore = rolePreferenceScore(existing.candidateRole, preferredRole);
	const nextScore = rolePreferenceScore(next.candidateRole, preferredRole);
	if (nextScore > existingScore) return true;
	if (nextScore < existingScore) return false;
	return next.rate > existing.rate;
}

/**
 * Mirror of BE sanitizeTs1V3SummaryEvaluationsForPreview:
 * keep only rate >= 1 so handshake/N/A cannot exclude OB maneuvers from A/B.
 */
export function sanitizeTs1V3EvaluationsForPreviewCalc(params: {
	candidateId: string;
	evaluations: Ts1V3PreviewEvaluationRow[];
	preferredRole?: string;
}): Ts1V3PreviewEvaluationRow[] {
	const { candidateId, evaluations, preferredRole } = params;
	const candidateRows = evaluations.filter(
		(row) => !row.candidateId || row.candidateId === candidateId,
	);
	const byAssessment = new Map<string, Ts1V3PreviewEvaluationRow>();

	for (const row of candidateRows) {
		if (row.rate < 1) continue;
		const existing = byAssessment.get(row.assessmentId);
		if (!existing) {
			byAssessment.set(row.assessmentId, row);
			continue;
		}
		if (shouldReplaceExisting(existing, row, preferredRole)) {
			byAssessment.set(row.assessmentId, row);
		}
	}

	return Array.from(byAssessment.values());
}

function outcomeValueToRate(value: ManeuverOutcomeValue | null | undefined): number | null {
	if (value == null) return null;
	if (value === "na") return -1;
	if (value === "inc") return 0;
	const num = Number(value);
	return Number.isFinite(num) ? num : null;
}

function slotToCandidateRole(slot: OutcomeSlot): CandidateRole {
	return slot === "pfPrimary" || slot === "pf" ? CandidateRole.PF : CandidateRole.PM;
}

/**
 * Build evaluation-shaped rows from Step 3 maneuverOutcomes for the carousel candidate.
 * PF/PM columns map to the same slots as the review grid.
 */
export function buildTs1V3PreviewEvaluationsFromManeuverOutcomes(params: {
	candidateId: string;
	candidateIndex: number;
	hasSecondCandidate: boolean;
	assessmentConfig: AssessmentConfigRes[];
	maneuverOutcomes: ManeuverOutcomeDraft[];
}): Ts1V3PreviewEvaluationRow[] {
	const { candidateId, candidateIndex, hasSecondCandidate, assessmentConfig, maneuverOutcomes } =
		params;
	const pfSlot = step3PfColumnOutcomeSlot(candidateIndex);
	const pmSlot = step3PmColumnOutcomeSlot(candidateIndex, hasSecondCandidate);
	const rows: Ts1V3PreviewEvaluationRow[] = [];

	for (const assessment of assessmentConfig ?? []) {
		const maneuverCode = assessment.maneuverType?.code;
		if (!maneuverCode) continue;
		const outcome = maneuverOutcomes.find((o) => o.maneuverCode === maneuverCode);
		if (!outcome) continue;

		for (const slot of [pfSlot, pmSlot] as OutcomeSlot[]) {
			const rate = outcomeValueToRate(outcome[slot]);
			if (rate == null) continue;
			rows.push({
				assessmentId: assessment.assessmentId,
				rate,
				candidateId,
				candidateRole: slotToCandidateRole(slot),
				maneuverCode,
			});
		}
	}

	return rows;
}

/**
 * Build evaluation rows from legacy temRate map (assessmentId-keyed).
 * Used as fallback when maneuverOutcomes are absent (e.g. incomplete staging).
 */
export function buildTs1V3PreviewEvaluationsFromTemRate(params: {
	candidateId: string;
	assessmentConfig: AssessmentConfigRes[];
	temRate: Record<string, { rate?: number; assessmentId?: string } | undefined>;
}): Ts1V3PreviewEvaluationRow[] {
	const { candidateId, assessmentConfig, temRate } = params;
	const rows: Ts1V3PreviewEvaluationRow[] = [];

	for (const [key, value] of Object.entries(temRate ?? {})) {
		if (value?.rate == null) continue;
		const [rateCandidateId, assessmentId] = key.split(".");
		if (rateCandidateId !== candidateId) continue;
		const maneuverCode =
			assessmentConfig.find((a) => a.assessmentId === assessmentId)?.maneuverType?.code ?? "";
		if (!maneuverCode) continue;
		rows.push({
			assessmentId,
			rate: value.rate,
			candidateId,
			candidateRole: null,
			maneuverCode,
		});
	}

	return rows;
}

/**
 * TS1-v3: maneuver codes to exclude from Rating A/B.
 * After sanitize (rate >= 1 only), N/A / Incomplete / handshake never exclude OB maneuvers —
 * matching BE Ts1V3SummaryService → getPreviewSummaryData.
 */
export function buildTs1V3ManeuverCodesToExclude(params: {
	candidateId: string;
	preferredRole?: string;
	evaluations?: Ts1V3PreviewEvaluationRow[];
	maneuverOutcomes?: ManeuverOutcomeDraft[];
	assessmentConfig?: AssessmentConfigRes[];
	candidateIndex?: number;
	hasSecondCandidate?: boolean;
	temRate?: Record<string, { rate?: number; assessmentId?: string } | undefined>;
}): string[] {
	const {
		candidateId,
		preferredRole,
		assessmentConfig = [],
		candidateIndex = 0,
		hasSecondCandidate = false,
		maneuverOutcomes,
		temRate,
	} = params;

	let evaluations = params.evaluations ?? [];
	if (!evaluations.length && maneuverOutcomes?.length) {
		evaluations = buildTs1V3PreviewEvaluationsFromManeuverOutcomes({
			candidateId,
			candidateIndex,
			hasSecondCandidate,
			assessmentConfig,
			maneuverOutcomes,
		});
	}
	if (!evaluations.length && temRate) {
		evaluations = buildTs1V3PreviewEvaluationsFromTemRate({
			candidateId,
			assessmentConfig,
			temRate,
		});
	}

	const sanitized = sanitizeTs1V3EvaluationsForPreviewCalc({
		candidateId,
		evaluations,
		preferredRole,
	});

	// Surviving rates are always >= 1 after sanitize; keep the < 1 guard for parity with BE.
	const excluded = new Set<string>();
	for (const row of sanitized) {
		if (row.rate < 1 && row.maneuverCode) {
			excluded.add(row.maneuverCode);
		}
	}
	return [...excluded];
}
